import React from 'react';
import PropTypes from 'prop-types';
import { formatAssociatedOrderNumberForDisplay, priceFormat, formatOrderDateToLocalTime } from '../../../util';
import {
  getSimplrAccountIdOfSelectedTicket,
  extractSelectedTicketBusinessDetailsFromState,
} from '../../../extractFromState';
import capitalize from 'lodash/capitalize';
import { REPLACEMENT_SOURCE_NAME } from '../../../constants/shopify';
import { connect } from 'react-redux';
import * as ToolsSelectors from '../../../selectors/tools';
import ProtectedData from '../../common/ProtectedData/ProtectedData';
import get from 'lodash/get';
import * as sensitiveData from 'constants/sensitiveDataTypes';
import {
  OrderDetailsPage,
  TableTd,
  OrderTable,
  Title,
  TitleImage,
  TitleRow,
  TableTr,
  TableTBody,
  Link,
} from '../OrderDetails.styles';

const NA = 'Not Available';

class ShopifyOrderDetails extends React.Component {
  get order() {
    return this.props.selectedOrder || {};
  }

  get orderStatus() {
    if (this.order['cancelled_at']) {
      return 'Cancelled';
    }

    if (this.order['closed_at']) {
      return 'Closed';
    }

    return 'Open';
  }

  get shippingPrice() {
    const totalShippingPrice = this.order['total_shipping_price_set'];
    const shopMoney = (totalShippingPrice && totalShippingPrice['shop_money']) || {};
    if (shopMoney && shopMoney.amount && this.props.businessDetail.useDiscountApplications) {
      const discountApplications = this.order['discount_applications'];
      let result = parseFloat(shopMoney.amount);

      discountApplications.forEach((discount) => {
        if (
          discount.type === 'script' &&
          discount.target_type === 'shipping_line' &&
          discount.value_type === 'fixed_amount'
        ) {
          result -= parseFloat(discount.value);
        }
      });

      return result;
    }

    return (shopMoney && shopMoney.amount) || NA;
  }

  get discountAmount() {
    const discountCodeAmount = (this.order['discount_codes'] || []).reduce(
      (total, { amount }) => total + parseFloat(amount),
      0
    );

    if (this.props.businessDetail.useDiscountApplications) {
      const discountApplications = this.order['discount_applications'];
      let result = discountCodeAmount;

      discountApplications.forEach((discount) => {
        if (
          discount.type === 'script' &&
          discount.target_type === 'line_item' &&
          discount.value_type === 'fixed_amount'
        ) {
          result += parseFloat(discount.value);
        }
      });

      return result;
    }

    return discountCodeAmount;
  }

  renderRefund() {
    const refundStrings = (this.order['refunds'] || []).reduce((refundResults, refund) => {
      if (refund.transactions) {
        refund.transactions.forEach((transaction) => {
          refundResults.push(
            `${formatOrderDateToLocalTime(transaction.created_at, 'shopify')}: $${transaction.amount}`
          );
        });
      }

      return refundResults;
    }, []);

    if (refundStrings.length) {
      return refundStrings.map((refundString, index) => {
        return (
          <TableTr key={'refund-' + refundString}>
            <TableTd>{index === 0 ? 'Refunds' : ''}</TableTd>
            <TableTd>{refundString}</TableTd>
          </TableTr>
        );
      });
    }

    return null;
  }

  renderLineItems() {
    const lineItemResults = (this.order['line_items'] || []).reduce((_lineItemResults, lineItem) => {
      _lineItemResults.push({
        lineItemTitle: `${lineItem.quantity} "${lineItem.title}": $${lineItem.price}`,
        lineItemDescription:
          lineItem.variant_title || lineItem.sku ? `${lineItem.variant_title} (SKU: ${lineItem.sku || NA})` : '',
      });

      return _lineItemResults;
    }, []);

    if (lineItemResults.length) {
      return lineItemResults.map((lineItemResult, index) => {
        return [
          <TableTr key={'lineItem-' + lineItemResult.lineItemTitle}>
            <TableTd>{index === 0 ? 'Order Items' : ''}</TableTd>
            <TableTd>{lineItemResult.lineItemTitle}</TableTd>
          </TableTr>,
          lineItemResult.lineItemDescription && (
            <TableTr key={'lineItem2-' + lineItemResult.lineItemDescription}>
              <TableTd />
              <TableTd style={{ paddingLeft: '3.5rem' }}>{lineItemResult.lineItemDescription}</TableTd>
            </TableTr>
          ),
        ];
      });
    }

    return (
      <TableTr>
        <TableTd>Order Items</TableTd>
        <TableTd>{NA}</TableTd>
      </TableTr>
    );
  }

  isShipstationTrackingNumberExist() {
    const shipstationOrder = this.order['shipstationOrder'];

    return Boolean(((shipstationOrder && shipstationOrder.fulfillments) || []).length);
  }

  isShopifyTrackingNumberExist() {
    return Boolean((this.order['fulfillments'] || []).filter(({ tracking_number }) => Boolean(tracking_number)).length);
  }

  renderNATrackingNumber() {
    if (!this.isShipstationTrackingNumberExist() && !this.isShopifyTrackingNumberExist()) {
      return (
        <TableTr>
          <TableTd>Tracking Number</TableTd>
          <TableTd>{NA}</TableTd>
        </TableTr>
      );
    }

    return null;
  }

  renderShipstationTrackingNumber() {
    if (this.isShipstationTrackingNumberExist()) {
      const shipstationOrder = this.order['shipstationOrder'];

      return shipstationOrder.fulfillments
        .filter(({ trackingNumber }) => Boolean(trackingNumber))
        .map(({ serviceCode, trackingNumber, carrierCode }, index) => {
          carrierCode = carrierCode || shipstationOrder.carrierCode;
          serviceCode = serviceCode || shipstationOrder.serviceCode;

          return (
            <TableTr key={`shipstationTrackingNumber-${trackingNumber}`}>
              <TableTd>{index === 0 ? 'Tracking Number' : ''}</TableTd>
              <TableTd>
                <div>Tracking number: {trackingNumber}</div>
                {carrierCode && <div>Carrier code: "{carrierCode}"</div>}
                {serviceCode && <div>Service code: "{serviceCode}"</div>}
              </TableTd>
            </TableTr>
          );
        });
    }

    return null;
  }

  renderShopifyTrackingNumber() {
    if (this.isShopifyTrackingNumberExist()) {
      const trackingNumberObjects = (this.order['fulfillments'] || [])
        .filter(({ tracking_number }) => Boolean(tracking_number))
        .reduce((acc, fulfillment) => {
          const lineItemsStrings = (fulfillment.line_items || []).map((lineItem) => {
            return `${lineItem.quantity} "${lineItem.title}": $${lineItem.price}`;
          });

          acc.push({
            id: fulfillment.id,
            number: fulfillment.tracking_number,
            url: fulfillment.tracking_url,
            lineItems: lineItemsStrings,
          });

          return acc;
        }, []);

      return trackingNumberObjects.map((trackingNumberObject, index) => {
        return (
          <TableTr key={`trackingNumber-${trackingNumberObject.id}`}>
            <TableTd>{index === 0 ? 'Tracking Number' : ''}</TableTd>
            <TableTd>
              {trackingNumberObject.lineItems.map((lineItemString) => (
                <div key={lineItemString}>{lineItemString}</div>
              ))}
              <Link
                style={{ marginLeft: '10px' }}
                href={trackingNumberObject.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {trackingNumberObject.number}
              </Link>
            </TableTd>
          </TableTr>
        );
      });
    }

    return null;
  }

  render() {
    const customer = this.order['customer'];
    const currency = this.order['currency'] || 'USD';
    const firstName = customer && customer.first_name;
    const lastName = customer && customer.last_name;
    const fullNameExist = !!firstName && !!lastName;
    const customerEmail = this.order.email;
    const orderNote = this.order.note;
    const shippingCountry = get(this.order, 'shipping_address.country', NA);
    const shippingProvince = get(this.order, 'shipping_address.province', NA);
    const shippingZip = get(this.order, 'shipping_address.zip', NA);
    const componentName = 'ShopifyOrderDetails';

    return (
      <OrderDetailsPage className="order-details-page">
        <TitleRow className="title-row">
          <div>
            <Title className="title">Order Summary</Title>
            <span>
              <TitleImage className="svg title-image" alt="" src="/images/green-clipboard.svg" />
            </span>
          </div>
        </TitleRow>

        <hr />
        <OrderTable className="order-table">
          <TableTBody>
            <TableTr>
              <TableTd>Store Name</TableTd>
              <TableTd>{this.order.shopName || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Order Number</TableTd>
              <TableTd>{formatAssociatedOrderNumberForDisplay(this.order.name) || NA}</TableTd>
            </TableTr>
            {this.order['source_name'] === REPLACEMENT_SOURCE_NAME && (
              <TableTr>
                <TableTd>Is Replacement Order</TableTd>
                <TableTd>Yes</TableTd>
              </TableTr>
            )}
            <TableTr>
              <TableTd>Order Note</TableTd>
              <TableTd>
                {orderNote ? (
                  <ProtectedData data={orderNote} dataType={sensitiveData.orderNote} component={componentName} />
                ) : (
                  NA
                )}
              </TableTd>
            </TableTr>

            <TableTr>
              <TableTd>Ordered At</TableTd>
              <TableTd>{formatOrderDateToLocalTime(this.order['created_at'], 'shopify') || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Customer Name</TableTd>
              <TableTd>
                {/* Always show first name and hide last name or display N/A if name doesn't exist  */}
                {firstName && firstName}{' '}
                {lastName && (
                  <ProtectedData data={lastName} dataType={sensitiveData.lastName} component={componentName} />
                )}
                {!fullNameExist && NA}
              </TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Customer Email</TableTd>
              <TableTd>
                {customerEmail ? (
                  <ProtectedData data={customerEmail} dataType={sensitiveData.email} component={componentName} />
                ) : (
                  NA
                )}
              </TableTd>
            </TableTr>
            {this.renderLineItems()}
            {this.renderShipstationTrackingNumber()}
            {this.renderShopifyTrackingNumber()}
            {this.renderNATrackingNumber()}
            <TableTr>
              <TableTd>Subtotal</TableTd>
              <TableTd>
                {this.order['total_line_items_price'] ? `$${this.order['total_line_items_price']}` : NA}
              </TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Discount</TableTd>
              <TableTd>{priceFormat(this.discountAmount, currency)}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Shipping</TableTd>
              <TableTd>{priceFormat(this.shippingPrice, currency)}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Tax</TableTd>
              <TableTd>{this.order['total_tax'] ? `$${this.order['total_tax']}` : NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Total</TableTd>
              <TableTd>{this.order['total_price'] ? `$${this.order['total_price']}` : NA}</TableTd>
            </TableTr>

            {this.renderRefund()}

            <TableTr>
              <TableTd>Order Status</TableTd>
              <TableTd>{this.orderStatus}</TableTd>
            </TableTr>
            <TableTr className={this.order['cancelled_at'] ? '' : 'hidden'}>
              <TableTd>Cancelled At</TableTd>
              <TableTd>{formatOrderDateToLocalTime(this.order['cancelled_at'], 'shopify')}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Payment Status</TableTd>
              <TableTd>{capitalize(this.order['financial_status']) || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Shipment Status</TableTd>
              <TableTd>{capitalize(this.order['fulfillment_status']) || 'Unfulfilled'}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Customer Tags</TableTd>
              <TableTd>{this.order['tags'] || NA}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Shipping Country</TableTd>
              <TableTd>{shippingCountry}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Shipping State</TableTd>
              <TableTd>{shippingProvince}</TableTd>
            </TableTr>
            <TableTr>
              <TableTd>Shipping ZIP</TableTd>
              <TableTd>{shippingZip}</TableTd>
            </TableTr>
          </TableTBody>
        </OrderTable>
      </OrderDetailsPage>
    );
  }
}

ShopifyOrderDetails.propTypes = {
  selectedOrder: PropTypes.shape({
    name: PropTypes.string,
    customer: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
    }),
    email: PropTypes.string,
    subtotal_price: PropTypes.string,
    total_tax: PropTypes.string,
    total_price: PropTypes.string,
    financial_status: PropTypes.string,
    fulfillment_status: PropTypes.string,
    cancelled_at: PropTypes.string,
    closed_at: PropTypes.string,
    fulfillments: PropTypes.arrayOf(
      PropTypes.shape({
        tracking_number: PropTypes.string,
      })
    ),
    line_items: PropTypes.arrayOf(
      PropTypes.shape({
        quantity: PropTypes.number,
        title: PropTypes.string,
        price: PropTypes.string,
      })
    ),
    shipping_address: PropTypes.shape({
      name: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      province_code: PropTypes.string,
      zip: PropTypes.string,
    }),
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    businessDetail: extractSelectedTicketBusinessDetailsFromState(state),
    selectedOrder: ToolsSelectors.getSelectedOrder(state),
    simplrAccountPtrId: getSimplrAccountIdOfSelectedTicket(state),
  };
}

export default connect(mapStateToProps)(ShopifyOrderDetails);
